import { skipToken, useMutation } from '@apollo/client'
import { Loader } from 'lucide-react'
import { Suspense, useEffect, useState, useTransition } from 'react'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { ToggleMandatorySsoDialogFallback } from '@/routes/single-sign-on/mandatory-sso/toggle-mandatory-sso-dialog/fallback'
import type {
  DisableMandatorySsoMutation,
  DisableMandatorySsoMutationVariables,
  EnableMandatorySsoMutation,
  EnableMandatorySsoMutationVariables,
  ToggleMandatorySsoQuery,
  ToggleMandatorySsoQueryVariables,
} from '@/routes/single-sign-on/mandatory-sso/toggle-mandatory-sso-dialog/index.types.gen'

const TOGGLE_MANDATORY_SSO_QUERY = gql`
  query ToggleMandatorySso($idCompany: String!) {
    company(id: $idCompany) {
      id
      companyOwner: users(idRole: "company-owner") {
        user {
          id
          firstName
          lastName
          capabilities {
            id
            isSamlExempt
          }
        }
      }
      users {
        id
        capabilities @live {
          id
          isSamlExempt
        }
        user {
          id
          email
          firstName
          lastName
        }
      }
      samlSetting @live {
        id
        domain
        isForced
        isEnabled
      }
      draftSamlSetting: samlSetting(isDraft: true) @live {
        id
        isForced
        domain
      }
    }
  }
`

const ENABLE_MANDATORY_SSO_MUTATION = gql`
  mutation EnableMandatorySso(
    $idCompany: String!
    $shouldAddOwnerAsException: Boolean
  ) {
    enableForceSaml(
      idCompany: $idCompany
      shouldAddOwnerAsException: $shouldAddOwnerAsException
    ) {
      id
      isForced
    }
  }
`

const DISABLE_MANDATORY_SSO_MUTATION = gql`
  mutation DisableMandatorySso($idCompany: String!) {
    disableForceSaml(idCompany: $idCompany) {
      id
      isForced
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const DialogContent = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const [isPending, startTransition] = useTransition()
  const [shouldAddOwnerAsException, setShouldAddOwnerAsException] = useState<
    boolean | undefined
  >()

  const { data } = useSuspenseQuery<
    ToggleMandatorySsoQuery,
    ToggleMandatorySsoQueryVariables
  >(
    TOGGLE_MANDATORY_SSO_QUERY,
    isOpen ? { variables: { idCompany } } : skipToken
  )

  const liveSamlSetting = data?.company?.samlSetting
  const draftSamlSetting = data?.company?.draftSamlSetting
  const isSamlEnabled = liveSamlSetting?.isEnabled ?? false
  const isSsoMandatory =
    liveSamlSetting?.isForced ?? draftSamlSetting?.isForced ?? false
  const users = data?.company?.users ?? []
  const samlDomain = liveSamlSetting?.domain ?? draftSamlSetting?.domain ?? ''
  const numberOfNonCompliantSamlUsers = samlDomain
    ? users.filter(({ user, capabilities }) => {
        const isExempt = capabilities?.some(
          (capability) => capability.isSamlExempt
        )
        const email = user?.email
        const domain = email?.split('@')[1]
        return !isExempt && domain?.toLowerCase() !== samlDomain.toLowerCase()
      }).length
    : 0
  const companyOwner = data?.company?.companyOwner?.[0]?.user
  const companyOwnerFullName = companyOwner
    ? `${companyOwner.firstName} ${companyOwner.lastName}`
    : ''
  const isOwnerExempt = Boolean(
    companyOwner?.capabilities?.some((capability) => capability.isSamlExempt)
  )

  const [disableForceSaml] = useMutation<
    DisableMandatorySsoMutation,
    DisableMandatorySsoMutationVariables
  >(DISABLE_MANDATORY_SSO_MUTATION)

  const [enableForceSaml] = useMutation<
    EnableMandatorySsoMutation,
    EnableMandatorySsoMutationVariables
  >(ENABLE_MANDATORY_SSO_MUTATION)

  useEffect(() => {
    if (shouldAddOwnerAsException === undefined) {
      setShouldAddOwnerAsException(!isOwnerExempt)
    }
  }, [isOwnerExempt, shouldAddOwnerAsException, setShouldAddOwnerAsException])

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {isSsoMandatory
            ? 'Disable mandatory single sign-on'
            : 'Enable mandatory single sign-on'}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {isSsoMandatory ? (
            'Once disabled, users will not be required to sign in with SAML SSO. All users will be able to use an email address and password.'
          ) : (
            <>
              Once enabled, users will be required to sign in with SAML SSO.
              Only users added as exceptions will be able to sign in with an
              email address and password.
            </>
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>
      {!isSsoMandatory &&
        isSamlEnabled &&
        numberOfNonCompliantSamlUsers > 0 && (
          <Alert variant="destructive">
            <AlertDescription>
              <strong>Users will get locked out</strong>
              <p className="mt-2">
                If you enable mandatory SSO now,{' '}
                {numberOfNonCompliantSamlUsers === 1
                  ? '1 user'
                  : `${numberOfNonCompliantSamlUsers} users`}{' '}
                will not be able to access this Sevalla company account. This is
                because they do not use a {samlDomain} email address.
              </p>
              <p className="mt-2">Ways to resolve this:</p>
              <ul className="list-disc ml-6 mt-1">
                <li>
                  They can change their email address to a {samlDomain} one.
                </li>
                <li>You can add them as exceptions.</li>
                <li>You can remove them from user management.</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}

      {!isSsoMandatory && !isOwnerExempt && (
        <div className="space-y-4">
          <Alert>
            <AlertDescription>
              We recommend that you have at least one user on the exceptions
              list, in case there is an issue with your identity provider and
              you need to regain access to your account. The Company Owner is a
              good default choice for that.
            </AlertDescription>
          </Alert>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="add-owner-exception"
              checked={shouldAddOwnerAsException}
              onCheckedChange={(checked) =>
                setShouldAddOwnerAsException(Boolean(checked))
              }
              disabled={isPending}
            />
            <label
              htmlFor="add-owner-exception"
              className="text-sm cursor-pointer"
            >
              Add Company Owner ({companyOwnerFullName}) to exceptions
            </label>
          </div>
        </div>
      )}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isPending} onClick={() => onClose()}>
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction
          disabled={isPending}
          onClick={() => {
            startTransition(async () => {
              try {
                if (isSsoMandatory) {
                  await disableForceSaml({ variables: { idCompany } })
                } else {
                  await enableForceSaml({
                    variables: { idCompany, shouldAddOwnerAsException },
                  })
                }
                toast(
                  <ToasterContent variant="success">
                    Successfully updated mandatory SSO settings
                  </ToasterContent>,
                  { position: 'top-right' }
                )
                onClose()
              } catch {
                toast(
                  <ToasterContent variant="error">
                    Failed to update mandatory SSO settings
                  </ToasterContent>,
                  { position: 'top-right' }
                )
              }
            })
          }}
        >
          {isPending ? <Loader className="animate-spin" /> : null}
          Continue
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  )
}

export const ToggleMandatorySsoDialog = ({ isOpen, onClose }: Props) => {
  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent onEscapeKeyDown={onClose}>
        <Suspense fallback={<ToggleMandatorySsoDialogFallback />}>
          <DialogContent isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </AlertDialogContent>
    </AlertDialog>
  )
}
